/**
 * Space Invaders engine — pure game logic for the hidden arcade easter egg (C).
 * No DOM, no MobX. The component owns one state object from createGame() and
 * calls step() once per fixed tick; the renderer draws whatever is in state.
 * All coordinates are logical pixels on a 224×256 playfield (the arcade grid).
 */

export const WIDTH = 224;
export const HEIGHT = 256;
export const TICK_MS = 1000 / 60;

export const PLAYER_W = 13;
export const PLAYER_H = 8;
export const PLAYER_Y = 232;
export const PLAYER_SPEED = 1.5;
export const FIRE_COOLDOWN_MS = 300;
export const RESPAWN_MS = 1000;

export const SHOT_W = 1;
export const SHOT_H = 4;
export const SHOT_SPEED = 4;

export const BOMB_W = 2;
export const BOMB_H = 5;
export const BOMB_SPEED = 1;
export const MAX_BOMBS = 3;
export const BOMB_INTERVAL_MS = 800;
export const BOMB_INTERVAL_MIN_MS = 250;

export const INVADER_W = 12;
export const INVADER_H = 8;
export const INVADER_COLS = 11;
export const INVADER_ROWS = 5;
export const INVADER_PITCH_X = 16;
export const INVADER_PITCH_Y = 16;
export const BLOCK_START_X = 26;
export const BLOCK_START_Y = 48;
export const BLOCK_STEP_X = 2;
export const BLOCK_DROP_Y = 8;
export const EDGE_MARGIN = 2;
export const MAX_WAVE_DROP_ROWS = 4;
export const STEP_MS_MAX = 700;
export const STEP_MS_MIN = 60;
export const STEP_MS_FLOOR = 40;
export const WAVE_SPEED_FACTOR = 0.9;
export const POINTS = [10, 20, 30];

export const UFO_W = 16;
export const UFO_H = 7;
export const UFO_Y = 20;
export const UFO_SPEED = 0.5;
export const UFO_MIN_MS = 20000;
export const UFO_MAX_MS = 30000;
export const UFO_VALUES = [50, 100, 150, 300];

export const SHIELD_W = 22;
export const SHIELD_H = 16;
export const SHIELD_Y = 200;
export const SHIELD_XS = [17, 73, 129, 185];
// Inclusive cell ranges carved out of every fresh shield (the classic arch).
const SHIELD_NOTCH = {
    x0: 8, x1: 13, y0: 11, y1: 15,
};

export function overlaps(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function typeForRow(row) {
    if (row === 0) {
        return 2;
    }
    if (row <= 2) {
        return 1;
    }
    return 0;
}

export function computeStepMs(aliveCount, wave) {
    const total = INVADER_COLS * INVADER_ROWS;
    const dead = Math.max(0, Math.min(total - 1, total - aliveCount));
    const t = dead / (total - 1);
    const base = STEP_MS_MAX + (STEP_MS_MIN - STEP_MS_MAX) * t;
    const scaled = base * (WAVE_SPEED_FACTOR ** (Math.max(1, wave) - 1));
    return Math.max(STEP_MS_FLOOR, scaled);
}

export function makeShield(x) {
    const cells = new Uint8Array(SHIELD_W * SHIELD_H);
    for (let cy = 0; cy < SHIELD_H; cy += 1) {
        for (let cx = 0; cx < SHIELD_W; cx += 1) {
            const inNotch = cx >= SHIELD_NOTCH.x0 && cx <= SHIELD_NOTCH.x1
                && cy >= SHIELD_NOTCH.y0 && cy <= SHIELD_NOTCH.y1;
            const corner = (cy === 0 && (cx < 2 || cx >= SHIELD_W - 2))
                || (cy === 1 && (cx < 1 || cx >= SHIELD_W - 1));
            cells[cy * SHIELD_W + cx] = inNotch || corner ? 0 : 1;
        }
    }
    return {
        x, y: SHIELD_Y, w: SHIELD_W, h: SHIELD_H, cells,
    };
}

export function shieldCell(shield, cx, cy) {
    if (cx < 0 || cy < 0 || cx >= SHIELD_W || cy >= SHIELD_H) {
        return 0;
    }
    return shield.cells[cy * SHIELD_W + cx];
}

function erodeShield(shield, cx, cy) {
    for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
            const x = cx + dx;
            const y = cy + dy;
            if (x >= 0 && y >= 0 && x < SHIELD_W && y < SHIELD_H) {
                shield.cells[y * SHIELD_W + x] = 0;
            }
        }
    }
}

/**
 * True (and erodes a 3×3 blob) when the projectile touches a solid cell.
 * Shots travel up so they scan from the bottom row; bombs scan from the top.
 */
function hitShield(shield, rect, fromBelow) {
    if (!overlaps(rect, shield)) {
        return false;
    }
    const x0 = Math.max(0, Math.floor(rect.x - shield.x));
    const x1 = Math.min(SHIELD_W - 1, Math.ceil(rect.x + rect.w - shield.x) - 1);
    const y0 = Math.max(0, Math.floor(rect.y - shield.y));
    const y1 = Math.min(SHIELD_H - 1, Math.ceil(rect.y + rect.h - shield.y) - 1);
    const rows = [];
    for (let cy = y0; cy <= y1; cy += 1) {
        rows.push(cy);
    }
    if (fromBelow) {
        rows.reverse();
    }
    for (let r = 0; r < rows.length; r += 1) {
        const cy = rows[r];
        for (let cx = x0; cx <= x1; cx += 1) {
            if (shield.cells[cy * SHIELD_W + cx]) {
                erodeShield(shield, cx, cy);
                return true;
            }
        }
    }
    return false;
}

function nextUfoDelay(rng) {
    return UFO_MIN_MS + rng() * (UFO_MAX_MS - UFO_MIN_MS);
}

function aliveInvaders(state) {
    return state.invaders.filter(i => i.alive);
}

function centrePlayer(state) {
    state.player.x = Math.round((WIDTH - PLAYER_W) / 2);
}

export function startWave(state, wave) {
    const dropRows = Math.min(MAX_WAVE_DROP_ROWS, Math.max(0, wave - 1));
    const top = BLOCK_START_Y + dropRows * BLOCK_DROP_Y;
    const invaders = [];
    for (let row = 0; row < INVADER_ROWS; row += 1) {
        for (let col = 0; col < INVADER_COLS; col += 1) {
            invaders.push({
                row,
                col,
                type: typeForRow(row),
                x: BLOCK_START_X + col * INVADER_PITCH_X,
                y: top + row * INVADER_PITCH_Y,
                w: INVADER_W,
                h: INVADER_H,
                alive: true,
            });
        }
    }
    state.wave = wave;
    state.status = 'playing';
    state.invaders = invaders;
    state.invaderDir = 1;
    state.invaderStepMs = computeStepMs(invaders.length, wave);
    state.invaderTimerMs = 0;
    state.animFrame = 0;
    state.marchNote = 0;
    state.bombs = [];
    state.bombTimerMs = 0;
    state.bombIntervalMs = Math.max(BOMB_INTERVAL_MIN_MS, BOMB_INTERVAL_MS * (WAVE_SPEED_FACTOR ** (wave - 1)));
    state.playerShot = null;
    state.ufo = null;
    state.ufoTimerMs = nextUfoDelay(state.rng);
    state.shields = SHIELD_XS.map(makeShield);
    centrePlayer(state);
    state.player.cooldownMs = 0;
    state.player.respawnMs = 0;
    return state;
}

export function createGame({ rng = Math.random } = {}) {
    const state = {
        width: WIDTH,
        height: HEIGHT,
        rng,
        wave: 1,
        score: 0,
        lives: 3,
        status: 'playing',
        elapsedMs: 0,
        player: {
            x: 0, y: PLAYER_Y, w: PLAYER_W, h: PLAYER_H, cooldownMs: 0, respawnMs: 0,
        },
        playerShot: null,
        invaders: [],
        invaderDir: 1,
        invaderStepMs: STEP_MS_MAX,
        invaderTimerMs: 0,
        animFrame: 0,
        bombs: [],
        bombTimerMs: 0,
        bombIntervalMs: BOMB_INTERVAL_MS,
        shields: [],
        ufo: null,
        ufoTimerMs: 0,
        marchNote: 0,
        // Sound/FX events emitted by step(); the component drains this list
        // every frame (events.splice(0)). Plain data keeps the engine pure.
        events: [],
    };
    return startWave(state, 1);
}

function moveBlock(state) {
    const alive = aliveInvaders(state);
    if (alive.length === 0) {
        return;
    }
    let minX = Infinity;
    let maxX = -Infinity;
    alive.forEach(i => {
        minX = Math.min(minX, i.x);
        maxX = Math.max(maxX, i.x + i.w);
    });
    const dx = state.invaderDir * BLOCK_STEP_X;
    if (minX + dx < EDGE_MARGIN || maxX + dx > WIDTH - EDGE_MARGIN) {
        state.invaderDir = -state.invaderDir;
        alive.forEach(i => { i.y += BLOCK_DROP_Y; });
    } else {
        alive.forEach(i => { i.x += dx; });
    }
    state.animFrame = state.animFrame ? 0 : 1;
    state.events.push({ type: 'march', note: state.marchNote });
    state.marchNote = (state.marchNote + 1) % 4;
}

function dropBomb(state) {
    const bottomByCol = new Map();
    state.invaders.forEach(i => {
        if (!i.alive) {
            return;
        }
        const current = bottomByCol.get(i.col);
        if (!current || i.row > current.row) {
            bottomByCol.set(i.col, i);
        }
    });
    const shooters = Array.from(bottomByCol.values());
    if (shooters.length === 0) {
        return;
    }
    const index = Math.min(shooters.length - 1, Math.floor(state.rng() * shooters.length));
    const shooter = shooters[index];
    state.bombs.push({
        x: shooter.x + Math.floor(shooter.w / 2),
        y: shooter.y + shooter.h,
        w: BOMB_W,
        h: BOMB_H,
    });
}

function endGame(state) {
    state.status = 'gameOver';
    state.events.push({ type: 'gameOver' });
}

function loseLife(state) {
    state.lives -= 1;
    state.bombs = [];
    state.playerShot = null;
    state.events.push({ type: 'playerHit' });
    if (state.lives <= 0) {
        state.lives = 0;
        endGame(state);
        return;
    }
    state.player.respawnMs = RESPAWN_MS;
    centrePlayer(state);
}

function fireShot(state) {
    const { player } = state;
    state.playerShot = {
        x: Math.round(player.x + player.w / 2),
        y: player.y - SHOT_H,
        w: SHOT_W,
        h: SHOT_H,
    };
    state.events.push({ type: 'shoot' });
}

function consumeShot(state) {
    state.playerShot = null;
    state.player.cooldownMs = FIRE_COOLDOWN_MS;
}

export function step(state, input = {}, dtMs = TICK_MS) {
    if (state.status !== 'playing') {
        return state;
    }
    state.elapsedMs += dtMs;
    const { player } = state;

    // Everything freezes while the cannon respawns.
    if (player.respawnMs > 0) {
        player.respawnMs = Math.max(0, player.respawnMs - dtMs);
        return state;
    }

    // Player
    if (input.left) {
        player.x = Math.max(0, player.x - PLAYER_SPEED);
    }
    if (input.right) {
        player.x = Math.min(WIDTH - player.w, player.x + PLAYER_SPEED);
    }
    if (player.cooldownMs > 0) {
        player.cooldownMs = Math.max(0, player.cooldownMs - dtMs);
    }
    if (input.fire && !state.playerShot && player.cooldownMs === 0) {
        fireShot(state);
    }
    if (state.playerShot) {
        state.playerShot.y -= SHOT_SPEED;
        if (state.playerShot.y + SHOT_H < 0) {
            consumeShot(state);
        }
    }

    // Invader block
    state.invaderTimerMs += dtMs;
    if (state.invaderTimerMs >= state.invaderStepMs) {
        state.invaderTimerMs = 0;
        moveBlock(state);
    }

    // Bombs
    state.bombTimerMs += dtMs;
    if (state.bombTimerMs >= state.bombIntervalMs && state.bombs.length < MAX_BOMBS) {
        state.bombTimerMs = 0;
        dropBomb(state);
    }
    state.bombs.forEach(b => { b.y += BOMB_SPEED; });
    state.bombs = state.bombs.filter(b => b.y < HEIGHT);

    // UFO
    if (!state.ufo) {
        state.ufoTimerMs -= dtMs;
        if (state.ufoTimerMs <= 0) {
            const dir = state.rng() < 0.5 ? 1 : -1;
            const valueIndex = Math.min(UFO_VALUES.length - 1, Math.floor(state.rng() * UFO_VALUES.length));
            state.ufo = {
                x: dir > 0 ? -UFO_W : WIDTH,
                y: UFO_Y,
                w: UFO_W,
                h: UFO_H,
                dir,
                value: UFO_VALUES[valueIndex],
            };
            state.ufoTimerMs = nextUfoDelay(state.rng);
            state.events.push({ type: 'ufoSpawn' });
        }
    } else {
        state.ufo.x += state.ufo.dir * UFO_SPEED;
        if (state.ufo.x > WIDTH || state.ufo.x + UFO_W < 0) {
            state.ufo = null;
            state.events.push({ type: 'ufoGone' });
        }
    }

    // Player shot collisions
    if (state.playerShot) {
        const shot = state.playerShot;
        const target = state.invaders.find(i => i.alive && overlaps(shot, i));
        if (target) {
            target.alive = false;
            state.score += POINTS[target.type];
            consumeShot(state);
            state.invaderStepMs = computeStepMs(aliveInvaders(state).length, state.wave);
            state.events.push({ type: 'invaderHit' });
        } else if (state.ufo && overlaps(shot, state.ufo)) {
            state.score += state.ufo.value;
            state.ufo = null;
            consumeShot(state);
            state.events.push({ type: 'ufoHit' });
        } else if (state.shields.some(s => hitShield(s, shot, true))) {
            consumeShot(state);
        }
    }

    // Bomb collisions
    const survivors = [];
    let playerHit = false;
    state.bombs.forEach(b => {
        if (overlaps(b, player)) {
            playerHit = true;
            return;
        }
        if (state.shields.some(s => hitShield(s, b, false))) {
            return;
        }
        survivors.push(b);
    });
    state.bombs = survivors;
    if (playerHit) {
        loseLife(state);
        if (state.status === 'gameOver') {
            return state;
        }
    }

    // Invaders reaching the cannon line
    if (state.invaders.some(i => i.alive && i.y + i.h >= player.y)) {
        endGame(state);
        return state;
    }

    // Wave cleared
    if (!state.invaders.some(i => i.alive)) {
        state.status = 'waveCleared';
        state.events.push({ type: 'waveCleared' });
    }
    return state;
}
